// src/components/chat/ChatWindow.jsx
// Ventana de conversación: lista de mensajes + editor con adjuntos.
// Incluye:
//  - Paginación hacia arriba (scroll al tope carga más).
//  - Auto-scroll inteligente (solo si ya estabas cerca del fondo).
//  - Indicador de "escribiendo…" vía socket.
//  - Edición y borrado del mensaje propio (ventana de 15 min en backend).
//  - Indicador de "leído por" en chat directo y "leído por N" en grupos.
//  - Preview inline de PDF; otros archivos como link.
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Spinner, Badge, Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import socket from '../../services/socket';
import {
    listMessages, sendMessage, markRead, editMessage, deleteMessage,
} from '../../services/chatApi';
import AuthAttachment from './AuthAttachment';
import ChatSettingsModal from './ChatSettingsModal';
import EmojiPicker from './EmojiPicker';

const MAX_FILES = 5;
const MAX_SIZE  = 50 * 1024 * 1024;
const PAGE_SIZE = 50;
const NEAR_BOTTOM_PX = 80;
const TYPING_DEBOUNCE_MS = 1500;

function formatTime(d) {
    const date = new Date(d);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function chatTitle(chat, meId) {
    if (chat.type === 'group') return chat.name;
    const other = (chat.participants || []).find(p => String(p._id) !== String(meId));
    return other?.name || 'Chat';
}

export default function ChatWindow({ chat, onBack, onChatChanged, onChatRemoved }) {
    const { user }              = useContext(AuthContext);
    const meId                    = user?.id;

    const [messages, setMessages] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore]   = useState(true);
    const [text, setText]         = useState('');
    const [files, setFiles]       = useState([]);
    const [sending, setSending]   = useState(false);

    const [editingId, setEditingId]     = useState(null);
    const [editText, setEditText]       = useState('');
    const [typingUsers, setTypingUsers] = useState({}); // { userId: { name, expiresAt } }

    // Búsqueda en la conversación.
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [matchPos,   setMatchPos]   = useState(0); // índice 0-based del match activo

    // Modal de configuración del chat.
    const [showSettings, setShowSettings] = useState(false);

    // Emoji picker y drag & drop.
    const [showEmoji, setShowEmoji] = useState(false);
    const [dragOver, setDragOver]   = useState(false);
    const dragCounter = useRef(0);
    const textareaRef = useRef(null);

    const scrollRef    = useRef(null);
    const readTimer    = useRef(null);
    const typingTimer  = useRef(null);
    const lastTypingSent = useRef(0);
    const wasNearBottom  = useRef(true);
    const chatIdRef      = useRef(null);
    useEffect(() => { chatIdRef.current = chat?._id || null; }, [chat?._id]);

    // Debounced markRead — usa chatIdRef para no requerir re-suscripción del listener.
    const scheduleMarkRead = useRef(() => {
        if (readTimer.current) clearTimeout(readTimer.current);
        readTimer.current = setTimeout(() => {
            const id = chatIdRef.current;
            if (id) markRead(id).catch(() => {});
        }, 500);
    }).current;

    // Cargar historial inicial.
    useEffect(() => {
        if (!chat?._id) return;
        setLoading(true);
        setMessages([]);
        setHasMore(true);
        setEditingId(null);
        setTypingUsers({});

        listMessages(chat._id, { limit: PAGE_SIZE })
            .then(data => {
                setMessages(data.reverse()); // viene desc → render asc
                setHasMore(data.length >= PAGE_SIZE);
            })
            .catch(() => setMessages([]))
            .finally(() => {
                setLoading(false);
                // tras cargar, ir al fondo
                requestAnimationFrame(() => {
                    const el = scrollRef.current;
                    if (el) el.scrollTop = el.scrollHeight;
                });
            });

        socket.emit('joinChat', chat._id);
        markRead(chat._id).catch(() => {});

        return () => { socket.emit('leaveChat', chat._id); };
    }, [chat?._id]);

    // Cargar más al hacer scroll arriba.
    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        wasNearBottom.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < NEAR_BOTTOM_PX;

        if (el.scrollTop < 40 && hasMore && !loadingMore && messages.length) {
            const oldest = messages[0]?.createdAt;
            if (!oldest) return;
            setLoadingMore(true);
            const prevHeight = el.scrollHeight;
            listMessages(chat._id, { before: oldest, limit: PAGE_SIZE })
                .then(older => {
                    if (!older.length) { setHasMore(false); return; }
                    setMessages(prev => [...older.reverse(), ...prev]);
                    if (older.length < PAGE_SIZE) setHasMore(false);
                    // mantener posición visual tras prepend
                    requestAnimationFrame(() => {
                        const e2 = scrollRef.current;
                        if (e2) e2.scrollTop = e2.scrollHeight - prevHeight;
                    });
                })
                .catch(() => {})
                .finally(() => setLoadingMore(false));
        }
    };

    // Eventos socket: nuevos mensajes, edición, borrado, lectura, typing.
    useEffect(() => {
        if (!chat?._id) return;

        const onCreated = ({ chatId, message }) => {
            if (String(chatId) !== String(chat._id)) return;
            setMessages(prev => {
                if (prev.some(m => String(m._id) === String(message._id))) return prev;
                return [...prev, message];
            });
            if (String(message.sender?._id || message.sender) !== String(meId)) {
                scheduleMarkRead();
            }
        };
        const onUpdated = ({ chatId, message }) => {
            if (String(chatId) !== String(chat._id)) return;
            setMessages(prev => prev.map(m =>
                String(m._id) === String(message._id) ? { ...m, ...message } : m,
            ));
        };
        const onDeleted = ({ chatId, messageId }) => {
            if (String(chatId) !== String(chat._id)) return;
            setMessages(prev => prev.filter(m => String(m._id) !== String(messageId)));
        };
        const onRead = ({ chatId, userId, at }) => {
            if (String(chatId) !== String(chat._id)) return;
            setMessages(prev => prev.map(m => {
                if ((m.readBy || []).some(r => String(r.user) === String(userId))) return m;
                return { ...m, readBy: [...(m.readBy || []), { user: userId, at }] };
            }));
        };
        const onTyping = ({ chatId, userId, isTyping }) => {
            if (String(chatId) !== String(chat._id)) return;
            if (String(userId) === String(meId)) return;
            const participant = (chat.participants || []).find(p => String(p._id) === String(userId));
            if (!participant) return;
            setTypingUsers(prev => {
                const next = { ...prev };
                if (isTyping) {
                    next[userId] = { name: participant.name, expiresAt: Date.now() + 4000 };
                } else {
                    delete next[userId];
                }
                return next;
            });
        };

        socket.on('messageCreated', onCreated);
        socket.on('messageUpdated', onUpdated);
        socket.on('messageDeleted', onDeleted);
        socket.on('messageRead',    onRead);
        socket.on('chatTyping',     onTyping);
        return () => {
            socket.off('messageCreated', onCreated);
            socket.off('messageUpdated', onUpdated);
            socket.off('messageDeleted', onDeleted);
            socket.off('messageRead',    onRead);
            socket.off('chatTyping',     onTyping);
            if (readTimer.current) clearTimeout(readTimer.current);
            if (typingTimer.current) clearTimeout(typingTimer.current);
        };
    }, [chat?._id, meId, chat?.participants, scheduleMarkRead]);

    // Limpiar typingUsers caducados.
    useEffect(() => {
        const t = setInterval(() => {
            setTypingUsers(prev => {
                const now = Date.now();
                let changed = false;
                const next = { ...prev };
                for (const k of Object.keys(next)) {
                    if (next[k].expiresAt < now) { delete next[k]; changed = true; }
                }
                return changed ? next : prev;
            });
        }, 1000);
        return () => clearInterval(t);
    }, []);

    // Auto-scroll inteligente: solo si el usuario ya estaba al fondo.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        if (wasNearBottom.current) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages.length]);

    // ── Composer ─────────────────────────────────────────────────────────────
    /** Acepta tanto un evento <input> como un FileList/Array directo (drag & drop). */
    const addFiles = (incoming) => {
        const picked = Array.from(incoming || []);
        if (!picked.length) return;
        if (picked.length + files.length > MAX_FILES) {
            toast.warn(`Máximo ${MAX_FILES} archivos por mensaje`);
            return;
        }
        const oversize = picked.find(f => f.size > MAX_SIZE);
        if (oversize) { toast.warn(`"${oversize.name}" supera 50 MB`); return; }
        setFiles(prev => [...prev, ...picked]);
    };

    const handleFiles = (e) => {
        addFiles(e.target.files);
        e.target.value = ''; // permite re-seleccionar el mismo archivo
    };

    // ── Drag & drop ──────────────────────────────────────────────────────────
    // Usamos contador en vez de booleano para que dragenter/leave de hijos no
    // hagan parpadear el overlay (evento se propaga por hijos al moverse).
    const onDragEnter = (e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        dragCounter.current += 1;
        setDragOver(true);
    };
    const onDragOver = (e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        dragCounter.current = Math.max(0, dragCounter.current - 1);
        if (dragCounter.current === 0) setDragOver(false);
    };
    const onDrop = (e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        dragCounter.current = 0;
        setDragOver(false);
        addFiles(e.dataTransfer.files);
    };

    // ── Copy / paste de imágenes desde el portapapeles ───────────────────────
    // Soporta Ctrl+V de captura de pantalla, screenshot copiado, imagen desde
    // navegador, etc. Si el clipboard también trae texto plano, lo dejamos pasar
    // (no preventDefault) salvo que haya files; así se puede pegar texto normal.
    const onPaste = (e) => {
        const items = e.clipboardData?.items || [];
        const fromClip = [];
        for (const item of items) {
            if (item.kind === 'file') {
                const f = item.getAsFile();
                if (f) fromClip.push(f);
            }
        }
        if (fromClip.length) {
            e.preventDefault();
            addFiles(fromClip);
        }
    };

    // ── Emoji picker ─────────────────────────────────────────────────────────
    const insertEmoji = (emoji) => {
        const ta = textareaRef.current;
        if (!ta) { setText(t => t + emoji); return; }
        const start = ta.selectionStart ?? text.length;
        const end   = ta.selectionEnd   ?? text.length;
        const next  = text.slice(0, start) + emoji + text.slice(end);
        setText(next);
        // Restaurar foco y posición del cursor justo después del emoji.
        requestAnimationFrame(() => {
            ta.focus();
            const pos = start + emoji.length;
            ta.setSelectionRange(pos, pos);
        });
    };

    const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

    const sendTyping = (isTyping) => {
        const now = Date.now();
        // Throttle: máx 1 emisión por segundo.
        if (isTyping && now - lastTypingSent.current < 1000) return;
        lastTypingSent.current = now;
        socket.emit('chatTyping', { chatId: chat._id, isTyping });
    };

    const onTextChange = (e) => {
        setText(e.target.value);
        sendTyping(true);
        if (typingTimer.current) clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => sendTyping(false), TYPING_DEBOUNCE_MS);
    };

    const handleSend = async (e) => {
        e?.preventDefault?.();
        const content = text.trim();
        if (!content && !files.length) return;
        setSending(true);
        try {
            const sent = await sendMessage(chat._id, { content, files });
            setText(''); setFiles([]);
            sendTyping(false);
            wasNearBottom.current = true; // al enviar, siempre seguimos al fondo
            setMessages(prev => prev.some(m => String(m._id) === String(sent._id)) ? prev : [...prev, sent]);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo enviar');
        } finally { setSending(false); }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    // ── Edición / borrado ───────────────────────────────────────────────────
    const startEdit = (m) => {
        setEditingId(m._id);
        setEditText(m.content || '');
    };
    const cancelEdit = () => { setEditingId(null); setEditText(''); };
    const saveEdit = async () => {
        const newText = editText.trim();
        if (!newText) { toast.warn('El mensaje no puede quedar vacío'); return; }
        try {
            const updated = await editMessage(chat._id, editingId, newText);
            setMessages(prev => prev.map(m => String(m._id) === String(updated._id) ? { ...m, ...updated } : m));
            cancelEdit();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo editar');
        }
    };
    const handleDelete = async (m) => {
        if (!window.confirm('¿Eliminar este mensaje?')) return;
        try {
            await deleteMessage(chat._id, m._id);
            setMessages(prev => prev.filter(x => String(x._id) !== String(m._id)));
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo eliminar');
        }
    };

    // ── Cálculos derivados ──────────────────────────────────────────────────
    const headerTitle = useMemo(() => chatTitle(chat, meId), [chat, meId]);
    const subtitle    = chat?.type === 'group'
        ? `${chat.participants?.length || 0} participantes`
        : ((chat?.participants || []).find(p => String(p._id) !== String(meId))?.role || '');

    const typingNames = Object.values(typingUsers).map(t => t.name);
    const typingLabel = typingNames.length === 0
        ? null
        : typingNames.length === 1
            ? `${typingNames[0]} está escribiendo…`
            : `${typingNames.length} personas están escribiendo…`;

    const otherIds = useMemo(
        () => (chat?.participants || []).filter(p => String(p._id) !== String(meId)).map(p => String(p._id)),
        [chat, meId],
    );

    // ── Búsqueda ────────────────────────────────────────────────────────────
    const trimmedSearch = searchTerm.trim();
    const matchIds = useMemo(() => {
        if (!trimmedSearch) return [];
        const q = trimmedSearch.toLowerCase();
        return messages
            .filter(m => (m.content || '').toLowerCase().includes(q))
            .map(m => String(m._id));
    }, [messages, trimmedSearch]);

    // Reset de posición al cambiar el término o el conjunto de matches.
    useEffect(() => { setMatchPos(0); }, [trimmedSearch]);
    useEffect(() => {
        if (matchPos >= matchIds.length) setMatchPos(0);
    }, [matchIds.length, matchPos]);

    // Hacer scroll al match activo cuando cambia.
    useEffect(() => {
        if (!matchIds.length) return;
        const id = matchIds[matchPos];
        if (!id) return;
        const el = document.getElementById(`msg-${id}`);
        if (el && scrollRef.current) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [matchPos, matchIds]);

    const closeSearch = () => { setSearchOpen(false); setSearchTerm(''); setMatchPos(0); };
    const nextMatch   = () => { if (matchIds.length) setMatchPos(p => (p + 1) % matchIds.length); };
    const prevMatch   = () => { if (matchIds.length) setMatchPos(p => (p - 1 + matchIds.length) % matchIds.length); };

    /** Devuelve el contenido del mensaje con <mark> en cada coincidencia. */
    const renderHighlighted = (txt) => {
        if (!trimmedSearch || !txt) return txt;
        const q = trimmedSearch;
        const parts = [];
        const lower = txt.toLowerCase();
        const qLower = q.toLowerCase();
        let i = 0;
        while (i < txt.length) {
            const found = lower.indexOf(qLower, i);
            if (found === -1) { parts.push(txt.slice(i)); break; }
            if (found > i) parts.push(txt.slice(i, found));
            parts.push(<mark key={found} style={{ background: '#fde68a', padding: 0 }}>{txt.slice(found, found + q.length)}</mark>);
            i = found + q.length;
        }
        return parts;
    };

    /** Para chat directo: ¿el otro ya leyó este mensaje?
     *  Para grupo: cuántos lo leyeron (sin contarme). */
    const readInfo = (m) => {
        const readers = (m.readBy || []).map(r => String(r.user)).filter(id => id !== String(meId));
        if (chat.type === 'direct') {
            const other = otherIds[0];
            return { directRead: !!other && readers.includes(other) };
        }
        const count = readers.filter(id => otherIds.includes(id)).length;
        return { groupReadCount: count, groupTotal: otherIds.length };
    };

    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, position: 'relative' }}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {dragOver && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(10, 74, 168, .10)',
                    border: '3px dashed var(--brand-700, #0a4aa8)',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--brand-700, #0a4aa8)',
                    fontSize: 18,
                    fontWeight: 700,
                    zIndex: 50,
                    pointerEvents: 'none',
                }}>
                    📎 Suelta los archivos para adjuntarlos
                </div>
            )}
            {/* Header */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto' }}>
                {onBack && (
                    <Button variant="link" size="sm" onClick={onBack} style={{ padding: 0, color: 'var(--c-text-2)' }}>← Atrás</Button>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--c-text-1)' }}>{headerTitle}</div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-4)' }}>{subtitle}</div>
                </div>
                <Button
                    variant="link"
                    size="sm"
                    onClick={() => setSearchOpen(v => !v)}
                    title="Buscar en la conversación"
                    style={{ padding: 4, color: searchOpen ? 'var(--brand-700, #0a4aa8)' : 'var(--c-text-3)' }}
                >🔍</Button>
                <Button
                    variant="link"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    title={chat?.type === 'group' ? 'Configurar grupo' : 'Información del chat'}
                    style={{ padding: 4, color: 'var(--c-text-3)' }}
                >⚙️</Button>
            </div>

            {searchOpen && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderBottom: '1px solid var(--c-border)', background: 'var(--c-card)' }}>
                    <Form.Control
                        autoFocus
                        size="sm"
                        placeholder="Buscar en mensajes…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); e.shiftKey ? prevMatch() : nextMatch(); }
                            if (e.key === 'Escape') { e.preventDefault(); closeSearch(); }
                        }}
                        style={{ borderRadius: 8 }}
                    />
                    <span style={{ fontSize: 12, color: 'var(--c-text-4)', minWidth: 60, textAlign: 'center' }}>
                        {trimmedSearch
                            ? (matchIds.length ? `${matchPos + 1} / ${matchIds.length}` : '0 / 0')
                            : ''}
                    </span>
                    <Button size="sm" variant="light" onClick={prevMatch} disabled={!matchIds.length} title="Anterior">↑</Button>
                    <Button size="sm" variant="light" onClick={nextMatch} disabled={!matchIds.length} title="Siguiente">↓</Button>
                    <Button size="sm" variant="light" onClick={closeSearch} title="Cerrar (Esc)">✕</Button>
                </div>
            )}

            {/* Messages */}
            <div ref={scrollRef} onScroll={onScroll}
                 style={{ flex: 1, overflowY: 'auto', padding: '16px', background: 'var(--c-bg)', minHeight: 0 }}>
                {loadingMore && (
                    <div style={{ textAlign: 'center', padding: 6 }}><Spinner animation="border" size="sm" /></div>
                )}
                {!hasMore && messages.length > 0 && (
                    <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-text-4)', padding: 4 }}>
                        — inicio de la conversación —
                    </div>
                )}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 24 }}><Spinner animation="border" size="sm" /></div>
                ) : messages.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'var(--c-text-4)', fontSize: 13, marginTop: 24 }}>
                        Sin mensajes aún. ¡Sé el primero en escribir!
                    </p>
                ) : (
                    messages.map(m => {
                        const mine = String(m.sender?._id || m.sender) === String(meId);
                        const isEditing = String(editingId) === String(m._id);
                        const info = readInfo(m);
                        const isActiveMatch = trimmedSearch && matchIds[matchPos] && String(matchIds[matchPos]) === String(m._id);

                        return (
                            <div key={m._id} id={`msg-${m._id}`} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                                <div style={{
                                    maxWidth: '72%',
                                    padding: '9px 13px',
                                    borderRadius: 14,
                                    borderTopLeftRadius: mine ? 14 : 4,
                                    borderTopRightRadius: mine ? 4 : 14,
                                    background: mine ? 'var(--brand-700, #0a4aa8)' : 'var(--c-msg-bg, #f1f5f9)',
                                    color: mine ? '#fff' : 'var(--c-text-1)',
                                    boxShadow: '0 1px 2px rgba(0,0,0,.06)',
                                    outline: isActiveMatch ? '2px solid #f59e0b' : 'none',
                                    outlineOffset: 2,
                                    position: 'relative',
                                }}>
                                    {chat.type === 'group' && !mine && (
                                        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 2, opacity: 0.85 }}>
                                            {m.sender?.name}
                                        </div>
                                    )}

                                    {isEditing ? (
                                        <>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={editText}
                                                onChange={e => setEditText(e.target.value)}
                                                style={{ fontSize: 13, marginBottom: 4 }}
                                            />
                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                                <Button size="sm" variant="light" onClick={cancelEdit}>Cancelar</Button>
                                                <Button size="sm" variant="success" onClick={saveEdit}>Guardar</Button>
                                            </div>
                                        </>
                                    ) : (
                                        m.content && (
                                            <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {trimmedSearch ? renderHighlighted(m.content) : m.content}
                                            </div>
                                        )
                                    )}

                                    {(m.attachments || []).map((a, i) => (
                                        <AuthAttachment
                                            key={i}
                                            chatId={chat._id}
                                            msgId={m._id}
                                            idx={i}
                                            att={a}
                                            ownColor={mine}
                                        />
                                    ))}

                                    <div style={{ fontSize: 10, marginTop: 4, textAlign: 'right', opacity: 0.7, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
                                        {m.editedAt && <span style={{ fontStyle: 'italic' }}>editado</span>}
                                        <span>{formatTime(m.createdAt)}</span>
                                        {mine && chat.type === 'direct' && (
                                            <span title={info.directRead ? 'Leído' : 'Enviado'} style={{ fontSize: 11 }}>
                                                {info.directRead ? '✓✓' : '✓'}
                                            </span>
                                        )}
                                        {mine && chat.type === 'group' && info.groupTotal > 0 && (
                                            <span title={`Leído por ${info.groupReadCount}/${info.groupTotal}`} style={{ fontSize: 11 }}>
                                                {info.groupReadCount === info.groupTotal && info.groupTotal > 0 ? '✓✓' : `✓ ${info.groupReadCount}/${info.groupTotal}`}
                                            </span>
                                        )}
                                    </div>

                                    {mine && !isEditing && (
                                        <Dropdown align="end" style={{ position: 'absolute', top: 2, right: -28 }}>
                                            <Dropdown.Toggle as="span" style={{ cursor: 'pointer', fontSize: 12, color: 'var(--c-text-4)', padding: '0 4px', userSelect: 'none' }}>
                                                ⋯
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => startEdit(m)}>Editar</Dropdown.Item>
                                                <Dropdown.Item onClick={() => handleDelete(m)} className="text-danger">Eliminar</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}

                {typingLabel && (
                    <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--c-text-4)', padding: '4px 6px' }}>
                        {typingLabel}
                    </div>
                )}
            </div>

            {/* Composer */}
            <Form onSubmit={handleSend} style={{ borderTop: '1px solid var(--c-border)', padding: 10, background: 'var(--c-card)', flex: '0 0 auto' }}>
                {files.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                        {files.map((f, i) => (
                            <Badge key={i} bg="light" text="dark" style={{ padding: '6px 8px', fontWeight: 500 }}>
                                📎 {f.name} <span role="button" onClick={() => removeFile(i)} style={{ cursor: 'pointer', marginLeft: 6 }}>✕</span>
                            </Badge>
                        ))}
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, position: 'relative' }}>
                    <label style={{ cursor: 'pointer', padding: '6px 10px', borderRadius: 8, background: 'var(--c-msg-bg, #f1f5f9)', fontSize: 18 }} title="Adjuntar archivo">
                        📎
                        <input type="file" multiple hidden onChange={handleFiles} />
                    </label>
                    <div style={{ position: 'relative' }}>
                        <button
                            type="button"
                            onClick={() => setShowEmoji(v => !v)}
                            title="Emojis"
                            style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--c-msg-bg, #f1f5f9)', fontSize: 18, border: 'none', cursor: 'pointer' }}
                        >
                            😀
                        </button>
                        {showEmoji && (
                            <EmojiPicker
                                onPick={(e) => insertEmoji(e)}
                                onClose={() => setShowEmoji(false)}
                            />
                        )}
                    </div>
                    <Form.Control
                        as="textarea"
                        ref={textareaRef}
                        rows={1}
                        placeholder="Escribe un mensaje…  (Enter para enviar, Shift+Enter para salto de línea, arrastrá o pegá archivos)"
                        value={text}
                        onChange={onTextChange}
                        onKeyDown={onKeyDown}
                        onPaste={onPaste}
                        style={{ resize: 'none', borderRadius: 10, fontSize: 14 }}
                    />
                    <Button type="submit" variant="primary" disabled={sending || (!text.trim() && !files.length)} style={{ borderRadius: 10 }}>
                        {sending ? <Spinner size="sm" animation="border" /> : 'Enviar'}
                    </Button>
                </div>
            </Form>

            <ChatSettingsModal
                show={showSettings}
                chat={chat}
                onClose={() => setShowSettings(false)}
                onChatChanged={(updated) => onChatChanged?.(updated)}
                onChatRemoved={(id) => onChatRemoved?.(id)}
            />
        </div>
    );
}
